import { Component, OnInit } from '@angular/core';
import { ArchivesService } from 'src/app/_services/archives.service';
import { VehiclesService } from 'src/app/_services/vehicles.service';
import { TechnicalViewService } from 'src/app/_services/technical-view.service';
import { IssuesService } from 'src/app/_services/issues.service';
import { MessageService } from 'primeng/api';
import {
  DynamicDialogConfig,
  DialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { CreateVehicleComponent } from '../vehicle-details/create-vehicle/create-vehicle.component';
import { FormBuilder } from '@angular/forms';
@Component({
  selector: 'app-archive',
  templateUrl: './archive.component.html',
  styleUrls: ['./archive.component.scss'],
})
export class ArchiveComponent implements OnInit {
  data: any;
  showingNext = false;
  archiveCount: any;
  showCreateVehicleDialog: boolean = false;
  technicalRecords: any[] = [];
  activeIssues: any[] = [];
  loadingIssues: boolean = false;

  // Odometer-based changeover detection: a sharp drop in the odometer series
  // (e.g. 1.5M km -> 50k) means the fleet number was put on a new truck. We
  // use the most recent such reset to pre-fill the archive date range for the
  // retiring truck (everything up to the changeover). Fully overridable.
  detecting = false;
  detectedResets: {
    changeoverDate: string;
    newSegmentDate: string;
    prevOdometer: number;
    newOdometer: number;
  }[] = [];
  detectionMessage = '';
  // First odometer reading of the new truck (the record just after the reset)
  // — offered as the new vehicle's in-service date in the create step.
  detectedInServiceDate = '';

  issueColumns = [
    { field: 'remarks', header: 'Remarks' },
    { field: 'description', header: 'Description' },
    { field: 'signoff_type', header: 'Sign Off Type' },
    { field: 'signed_off', header: 'Signed Off' },
    { field: 'fine_amount', header: 'Fine Amount' },
  ];

  technicalColumns = [
    { field: 'record_type', header: 'Record Type' },
    { field: 'odometer', header: 'Odometer' },
    { field: 'date_completed', header: 'Date Completed' },
    { field: 'chassis_no', header: 'Chassis Number' },
  ];

  getIssueColumns() {
    return this.issueColumns.length;
  }

  getTechnicalColumns() {
    return this.technicalColumns.length;
  }

  constructor(
    public archiveService: ArchivesService,
    private vehicleService: VehiclesService,
    private technicalService: TechnicalViewService,
    private issueService: IssuesService,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    public dialogService: DialogService,
    private fb: FormBuilder,
    private ref: DynamicDialogRef
  ) {}

  ngOnInit(): void {
    this.data = this.config.data.formdata;

    this.archiveService
      .getArchivedVehicles(this.data.vehicle_reference)
      .subscribe(
        (res: any) => {
          this.archiveCount = res.body + 1;
          this.archiveService.archiveForm
            .get('vehicle_reference')
            ?.setValue(
              this.data.vehicle_reference + '-' + this.archiveCount + 'A'
            );
        },
        (error) => {
          // console.log(error);
        }
      );

    this.detectChangeover();
  }

  /** Pull the vehicle's technical records, find the most recent odometer
   *  reset, and pre-fill both date ranges up to that changeover. */
  detectChangeover(): void {
    if (!this.data?.id) {
      return;
    }
    this.detecting = true;
    this.technicalService
      .getTechnicalRecordsByVehicleId(String(this.data.id), 1, 5000)
      .subscribe({
        next: (res: any) => {
          const body = res?.body || {};
          const records: any[] = Array.isArray(body)
            ? body
            : body.records || [];
          const series = records
            .filter((r) => r.odometer && r.date_completed)
            .map((r) => ({
              odo: Number(r.odometer),
              date: String(r.date_completed).slice(0, 10),
            }))
            .filter((r) => r.odo > 0)
            .sort((a, b) => a.date.localeCompare(b.date));

          // A reset: the reading drops to under half of the previous one and
          // by more than 50 000 km. That combination separates real truck
          // swaps from ordinary noise or a single mistyped reading.
          const resets: {
            changeoverDate: string;
            newSegmentDate: string;
            prevOdometer: number;
            newOdometer: number;
          }[] = [];
          for (let i = 1; i < series.length; i++) {
            const prev = series[i - 1];
            const cur = series[i];
            if (cur.odo < prev.odo * 0.5 && prev.odo - cur.odo > 50000) {
              resets.push({
                changeoverDate: prev.date, // last record of the retiring truck
                newSegmentDate: cur.date, // first record of the new truck
                prevOdometer: prev.odo,
                newOdometer: cur.odo,
              });
            }
          }

          this.detectedResets = resets;
          this.detecting = false;

          if (resets.length === 0) {
            this.detectionMessage =
              'No odometer changeover detected — set the dates manually if this vehicle is being archived as a whole.';
            return;
          }

          // Default to the earliest swap — the first/oldest truck to have
          // carried this fleet number — so successive archives peel history
          // off oldest-first (018-1A, 018-2A, …).
          const target = resets[0];
          const earliest = series[0].date;
          this.applyDetectedRange(earliest, target.changeoverDate);
          // Stamp the archived copy's own service window: in-service = the
          // retiring truck's first reading, out-of-service = the changeover.
          // The new truck comes into service at its first (post-reset) reading.
          this.archiveService.archiveForm.patchValue({
            date_inservice: earliest,
            date_outservice: target.changeoverDate,
          });
          this.detectedInServiceDate = target.newSegmentDate;
          const extra =
            resets.length > 1
              ? ` (${resets.length} swaps found — using the earliest; adjust the dates to pick a later one)`
              : '';
          this.detectionMessage =
            `Detected a new vehicle from ${target.changeoverDate} ` +
            `(odometer ${target.prevOdometer.toLocaleString()} → ` +
            `${target.newOdometer.toLocaleString()}). Pre-filled records up to ` +
            `the changeover${extra}.`;
        },
        error: () => {
          this.detecting = false;
          this.detectionMessage = '';
        },
      });
  }

  private applyDetectedRange(from: string, to: string): void {
    this.archiveService.dateRangeForm.patchValue({
      x_date_from: from,
      x_date_to: to,
      y_date_from: from,
      y_date_to: to,
    });
  }

  nextDialog() {
    this.showingNext = true;
    this.loadingIssues = true;

    // Active issues were previously never fetched here, so `activeIssues`
    // stayed permanently empty and CreateVehicleComponent's archive-flow
    // migration calls (archiveIssue/changeActiveIssueVehicleId) always ran
    // against an empty list - an archived vehicle's open issues were left
    // behind on the old vehicle_reference instead of moving with it.
    //
    // The two date ranges are independent, optional-per-section inputs -
    // leaving either one blank is a normal thing to do and the backend
    // 400s on a blank range for that section only. These are deliberately
    // two separate subscriptions (not forkJoin) so that a blank/invalid
    // range on one section just shows "none found" for that section
    // instead of losing the other section's already-successful results.
    let pendingRequests = 2;
    const onSettled = () => {
      pendingRequests -= 1;
      if (pendingRequests === 0) {
        this.loadingIssues = false;
      }
    };

    this.archiveService
      .getTechnicalRecordsByDateRange(
        this.archiveService.dateRangeForm.value,
        this.data.id
      )
      .subscribe({
        next: (res: any) => {
          this.technicalRecords = res.body;
          onSettled();
        },
        error: () => {
          this.technicalRecords = [];
          onSettled();
        },
      });

    this.archiveService
      .getActiveIssuesByDateRange(
        this.archiveService.dateRangeForm.value,
        this.data.id
      )
      .subscribe({
        next: (res: any) => {
          this.activeIssues = res.body;
          onSettled();
        },
        error: () => {
          this.activeIssues = [];
          onSettled();
        },
      });
  }

  returnToPreviousDialog() {
    this.showingNext = false;
  }

  showAddVehicleDialog() {
    this.showCreateVehicleDialog = true;
  }

  showCreateVehicle() {
    const ref = this.dialogService.open(CreateVehicleComponent, {
      header: 'Create Vehicle',
      width: '70%',
      styleClass: 'update-record-dialog-shell',
      data: {
        formdata: this.data,
      },
    });
  }

  closeDialog() {
    this.ref.close();
    this.archiveService.dateRangeForm.reset();
  }
}
